// rankingCache.js — the Engine screen's "Prepress ranking cache" card, as
// plain functions and copy, so `node src/rankingCache.test.js` can execute
// them (the same arrangement as capacity.js; see its header for why).
//
// WHAT THE CACHE IS. Prepress remembers which pairing won for a set of cartons,
// so the next export of the same cartons skips re-ranking. It is the only
// cache on the box that holds a decision. No LAYOUT is ever served from cache,
// so clearing it changes how long the next exports take, never what they make.
//
// WHAT THIS FILE REFUSES TO SAY: "empty". The backend clears under a lock and
// reports exactly how many decisions it removed, but an export already running
// can store a fresh one the instant the lock is released. `cleared` is a fact;
// "the cache is empty" would be a guess presented as one.

import { num } from './format.js';

export const CLEAR_RANKING_CACHE_COPY = {
  what: 'Prepress remembers which carton pairing won, so repeat exports skip '
      + 're-ranking. This is that memory. Layouts themselves are never cached.',
  title: 'Clear the prepress ranking cache?',
  body: [
    'This empties the pairing-decision memory for every organisation, not '
      + 'only one.',
    'The next exports re-rank their cartons, so they are slower until the '
      + 'memory refills.',
    'Layouts are never cached, so no result changes — only how long the next '
      + 'exports take.',
  ],
  confirmLabel: 'Clear ranking cache',
};

/** Rows for the card. Every value is the backend's; a missing one is null so
 *  the KV renders a dash rather than a confident zero. `scope` is the
 *  backend's sentence — the cache is per process, and the screen must not
 *  claim more than the process that answered can see. */
export function rankingCacheRows(stats) {
  const s = stats || {};
  const has = (k) => typeof s[k] === 'number';
  return [
    ['Entries', has('entries') && has('max_entries')
      ? `${num(s.entries)} / ${num(s.max_entries)}` : null],
    ['Hits', has('hits') ? num(s.hits) : null],
    ['Misses', has('misses') ? num(s.misses) : null],
    ['Scope', s.scope
      ? `${s.scope}${has('pid') ? ` (pid ${s.pid})` : ''}` : null],
  ];
}

/** What the operator is told after a clear. Reads `cleared` — the count the
 *  backend removed — and never `stats_after`, which is only a snapshot. */
export function clearedText(result) {
  const n = result?.cleared;
  if (typeof n !== 'number') return 'The backend did not say how many decisions it cleared.';
  return `Cleared ${num(n)} decision${n === 1 ? '' : 's'}. Exports already `
       + 'running may store new ones straight away.';
}
